"use client";

import { PriceAnchor } from "@/components/services/price-anchor";
import { useState, type ReactNode } from "react";
import Link from "next/link";
import { m } from "framer-motion";
import {
  Calendar,
  Send,
  ArrowRight,
  Check,
  X,
  Sparkles,
  TrendingUp,
  Megaphone,
  Search,
  Target,
  Users,
  BarChart3,
  Zap,
  MousePointerClick,
  CircleDollarSign,
} from "lucide-react";
import { AdsCampaignMockup } from "@/components/mockups/service-mockups";
/** Server-gerenderte Abschnitte des Seitenschemas (Auftrag 2026-10-28, Paket 7), vom page.tsx übergeben. */
export type ServiceSlots = {
  afterHero?: ReactNode;
  afterPrice?: ReactNode;
  beforeProcess?: ReactNode;
  /** Ablauf als gemeinsame Zeitleiste (ProcessSteps) – ersetzt den früheren eigenen Block. */
  process?: ReactNode;
  beforeCta?: ReactNode;
  /** Hintergrund des CTA-Abschnitts, damit der Wechsel weiß/grau auch mit und ohne FAQ aufgeht (Paket 12, Nr. 8a). */
  ctaTone?: "primary" | "secondary";
};


/* ─────────────────────────────────────────────────────────────
 * ROAS-Rechner
 *
 * Input: Budget €/Mo, CTR %, Conversion-Rate %, Ø Auftragswert €
 * Annahme: Klick-Preis (CPC) ≈ 1,20 € (Google-Schnitt Mittelstand DE)
 * Output: Klicks, Leads, Umsatz, ROAS
 * ──────────────────────────────────────────────────────────── */

const ASSUMED_CPC = 1.2; // € pro Klick (Google-Ø für mittelständische Branchen DE)

function RoasCalculator() {
  const [budget, setBudget] = useState(1500);
  const [convRate, setConvRate] = useState(4);
  const [aov, setAov] = useState(900);

  const clicks = Math.round(budget / ASSUMED_CPC);
  const leads = Math.round((clicks * convRate) / 100);
  const revenue = leads * aov;
  const roas = budget > 0 ? revenue / budget : 0;
  const profit = revenue - budget;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-5 lg:p-8"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--card-bg)",
        boxShadow: "var(--card-shadow-lg)",
      }}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-blue-400/10 blur-3xl" />

      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>
          <CircleDollarSign className="h-3.5 w-3.5" />
          ROAS-Rechner
        </span>
        <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>
          Was bringt Ihr Werbebudget – ehrlich gerechnet?
        </h2>
        <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
          Schieben Sie Ihr Budget, Ihre Conversion-Rate und Ihren Ø Auftragswert.
          Wir rechnen Klicks, Leads, Umsatz und <strong style={{ color: "var(--text)" }}>ROAS</strong> –
          basierend auf einem realistischen Google-Klickpreis von 1,20 €.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-2 lg:gap-10">
          <div className="space-y-6">
            <SliderField id="budget" label="Werbebudget / Monat" value={budget} min={300} max={20000} step={100} format={(v) => `${v.toLocaleString("de-DE")} €`} onChange={setBudget} accent="brand" />
            <SliderField id="conv" label="Conversion-Rate (Klick → Lead)" value={convRate} min={1} max={15} step={1} format={(v) => `${v} %`} onChange={setConvRate} accent="brand" />
            <SliderField id="aov" label="Ø Auftragswert (Lead)" value={aov} min={50} max={10000} step={50} format={(v) => `${v.toLocaleString("de-DE")} €`} onChange={setAov} accent="brand" />
            <div className="rounded-xl border p-4 text-xs leading-relaxed" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)", color: "var(--text-tertiary)" }}>
              <p>Klickpreis (CPC) Annahme: <strong>1,20 € pro Klick</strong> – branchen-üblich für Google Ads im deutschen Mittelstand. Conversion-Raten typisch: 2–5 % bei kalter Suche, 5–10 % bei warmer Zielgruppe.</p>
            </div>
          </div>

          <div className="flex flex-col justify-center gap-4 rounded-xl p-5 lg:p-6" style={{ backgroundColor: "var(--bg-secondary)" }}>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard label="Klicks/Mo" value={clicks.toLocaleString("de-DE")} Icon={MousePointerClick} />
              <KpiCard label="Leads/Mo" value={leads.toLocaleString("de-DE")} Icon={Target} />
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Erwarteter Umsatz / Mo</p>
              <p className="text-xl font-semibold tabular-nums" style={{ color: "var(--text)" }}>{revenue.toLocaleString("de-DE")} €</p>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Profit / Mo (vor Margen)</p>
              <p className="text-xl font-semibold tabular-nums" style={{ color: profit > 0 ? "var(--fi-success-fg)" : "var(--fi-error-fg)" }}>{profit.toLocaleString("de-DE")} €</p>
            </div>
            <div className="flex items-baseline justify-between gap-3 border-t pt-3" style={{ borderColor: "var(--border)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>ROAS</p>
              <p className="text-2xl font-bold tabular-nums sm:text-3xl" style={{ color: roas >= 2 ? "var(--fi-success-fg)" : roas >= 1 ? "var(--fi-warning-fg)" : "var(--fi-error-fg)" }}>{roas.toFixed(1)}×</p>
            </div>

            <div className="mt-3 rounded-lg border-l-4 p-4" style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--fi-brand-fg)" }}>Was guter ROAS heißt</p>
              <p className="mt-1 text-sm leading-snug" style={{ color: "var(--fi-brand-fg)" }}>
                <strong>1×</strong> = Break-Even · <strong>3×</strong> = solide ·{" "}
                <strong>5×+</strong> = sehr gut. Wir starten Kampagnen mit dem
                Ziel ROAS &gt; 3× innerhalb von 90 Tagen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, Icon }: { label: string; value: string; Icon: typeof MousePointerClick }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}>
      <Icon className="h-3.5 w-3.5 text-brand-500" />
      <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>{label}</p>
      <p className="mt-0.5 text-lg font-bold tabular-nums" style={{ color: "var(--text)" }}>{value}</p>
    </div>
  );
}

function SliderField({ id, label, value, min, max, step, format, onChange, accent }: { id: string; label: string; value: number; min: number; max: number; step: number; format: (v: number) => string; onChange: (v: number) => void; accent: "brand" | "error"; }) {
  const bg = accent === "brand" ? "var(--fi-brand-bg)" : "var(--fi-error-bg)";
  const fg = accent === "brand" ? "var(--fi-brand-fg)" : "var(--fi-error-fg)";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-semibold" style={{ color: "var(--text)" }}>{label}</label>
        <span className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums" style={{ backgroundColor: bg, color: fg }}>{format(value)}</span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseInt(e.target.value))} className="mt-2 w-full accent-brand-500" />
      <div className="mt-1 flex justify-between text-[10px]" style={{ color: "var(--text-quaternary)" }}>
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Plattform-Karten
 * ──────────────────────────────────────────────────────────── */
function PlatformCard({ Icon, name, audience, strengths, bg }: { Icon: typeof Search; name: string; audience: string; strengths: string[]; bg: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}>
      <div className="p-5" style={{ background: bg }}>
        <Icon className="h-6 w-6" style={{ color: "white" }} />
        <p className="mt-2 text-base font-bold text-white">{name}</p>
        <p className="text-[11px] text-white/85">{audience}</p>
      </div>
      <div className="p-5">
        <ul className="space-y-2">
          {strengths.map((s) => (
            <li key={s} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-500" />
              {s}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Vergleich
 * ──────────────────────────────────────────────────────────── */
function ComparisonBlock() {
  const without = [
    'Kampagne aufsetzen und „mal sehen"-Strategie',
    "Kein Conversion-Tracking – Sie sehen Klicks, keine Leads",
    "Breite Keywords ohne Match-Type → Geld an irrelevanten Traffic",
    "Ein generisches Ad-Set für alle Zielgruppen",
    "Keine Landingpage – Anzeige führt auf die Startseite",
  ];
  const withUs = [
    "Klare Hypothesen, A/B-Tests, monatliche Optimierung",
    "Sauberes Tracking (GA4, GTM, Conversion-API)",
    "Keyword-Strategie pro Match-Type, negative Keywords",
    "Eigene Ad-Sets pro Buyer-Persona / Zielgruppe",
    "Dedizierte Landingpage – konvertiert 2–5× besser",
  ];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-2xl border p-5 lg:p-6" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}>
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: "var(--fi-error-bg)", color: "var(--fi-error-fg)" }}>Geld verbrennen</span>
        <h3 className="mt-3 text-xl font-semibold" style={{ color: "var(--text)" }}>„Wir starten mal eine Anzeige."</h3>
        <ul className="mt-4 space-y-2">
          {without.map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--text-secondary)" }}>
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: "var(--fi-error-bg)" }}>
                <X className="h-3 w-3" style={{ color: "var(--fi-error-fg)" }} />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
      <div className="relative overflow-hidden rounded-2xl border p-5 lg:p-6" style={{ borderColor: "var(--color-brand-500)", backgroundColor: "var(--card-bg)", boxShadow: "var(--card-shadow-lg)" }}>
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-400/15 blur-2xl" />
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>
          <Sparkles className="h-3 w-3" /> DMD-Paid-Ads
        </span>
        <h3 className="mt-3 text-xl font-semibold" style={{ color: "var(--text)" }}>„Mein Budget arbeitet messbar."</h3>
        <ul className="mt-4 space-y-2">
          {withUs.map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--text)" }}>
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: "var(--fi-success-bg)" }}>
                <Check className="h-3 w-3" style={{ color: "var(--fi-success-fg)" }} />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function PaidAdsClient({ afterHero, afterPrice, beforeProcess, process, beforeCta, ctaTone = "secondary" }: ServiceSlots) {
  return (
    <>
      <nav className="mx-auto max-w-7xl px-4 py-4 lg:px-8" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-sm" style={{ color: "var(--text-tertiary)" }}>
          <li><Link href="/" className="transition-colors hover:text-brand-500">Startseite</Link></li>
          <li style={{ color: "var(--text-quaternary)" }}>/</li>
          <li><Link href="/dienstleistungen" className="transition-colors hover:text-brand-500">Dienstleistungen</Link></li>
          <li style={{ color: "var(--text-quaternary)" }}>/</li>
          <li className="font-medium" style={{ color: "var(--text)" }}>Paid Ads</li>
        </ol>
      </nav>

      <section className="pb-10 pt-4 lg:pb-12 lg:pt-8">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }}>
                <Megaphone className="h-3.5 w-3.5 text-brand-500" /> Google Ads · Meta Ads · LinkedIn Ads
              </div>
              <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>
                Anfragen <span className="text-brand-500">ab Tag 1</span> – nicht erst in 6 Monaten.
              </h1>
              <p className="mt-5 text-lg leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
                SEO ist Marathon – Paid Ads ist Sprint. Während Sie organisch
                wachsen, sorgen Google, Meta und LinkedIn dafür, dass{" "}
                <strong style={{ color: "var(--text)" }}>schon nächste Woche Anfragen</strong>{" "}
                reinkommen. Datengetrieben, monatlich kündbar, transparent abgerechnet.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  { Icon: Zap, text: "Sofort sichtbar" },
                  { Icon: BarChart3, text: "Conversion-Tracking" },
                  { Icon: TrendingUp, text: "ROAS-getrieben" },
                ].map((p) => {
                  const Icon = p.Icon;
                  return (
                    <span key={p.text} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }}>
                      <Icon className="h-3.5 w-3.5 text-brand-500" />
                      {p.text}
                    </span>
                  );
                })}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link href="/beratung" className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md">
                  <Calendar className="h-5 w-5" /> Kostenloses Erstgespräch
                </Link>
                <Link href="/anfrage" className="inline-flex items-center justify-center gap-2 rounded-lg border px-5 py-3 text-base font-semibold transition-all duration-150 hover:shadow-sm" style={{ color: "var(--text-secondary)", borderColor: "var(--border-secondary)" }}>
                  <Send className="h-4 w-4" /> Frage stellen
                </Link>
              </div>
            </div>
            <div>
              <AdsCampaignMockup />
            </div>
          </div>
        </div>
      </section>

      {afterHero}

      {/* Preisanker – Texte aus src/lib/facts.ts (PRICE_ANCHORS), bewusst ohne Beträge (Entscheidung 2026-10-21) */}
      <PriceAnchor service="paid-ads" />
      {afterPrice}

      {/* ROAS CALCULATOR (weiß, interaktiv) */}
      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <RoasCalculator />
        </div>
      </section>

      {/* PLATFORMS */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <m.div className="mx-auto max-w-2xl text-center" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.4 }}>
            <p className="text-sm font-semibold text-brand-500">Drei Plattformen</p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>Wo Ihre Zielgruppe wirklich klickt.</h2>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>Wir starten dort, wo Ihre Buyer-Persona aktiv ist – nicht überall gleichzeitig.</p>
          </m.div>
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <PlatformCard
              Icon={Search}
              name="Google Ads"
              audience="Menschen, die gerade aktiv nach Ihrer Leistung suchen"
              strengths={["Hohe Kaufabsicht (Such-Intent)", "Local Ads + Maps-Integration", "Performance Max & Display"]}
              bg="linear-gradient(135deg, #4285F4, #34A853)"
            />
            <PlatformCard
              Icon={Users}
              name="Meta Ads"
              audience="Demografisch + interessensbasierte B2C-Zielgruppen"
              strengths={["Riesige Reichweite (Insta + Facebook)", "Lookalike-Audiences vom Kundenstamm", "Storytelling im Reel- und Story-Format"]}
              bg="linear-gradient(135deg, #4267B2, #E1306C)"
            />
            <PlatformCard
              Icon={Target}
              name="LinkedIn Ads"
              audience="B2B-Entscheider, Jobtitel, Branchen, Firmengrößen"
              strengths={["Präzise Job-Title-Targeting", "Lead-Gen-Forms mit Auto-Fill", "Sponsored InMail an Entscheider"]}
              bg="linear-gradient(135deg, #0A66C2, #002F5C)"
            />
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <m.div className="mx-auto max-w-2xl text-center" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4 }}>
            <p className="text-sm font-semibold text-brand-500">Vorher / Nachher</p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>Budget verbrennen vs. datengetrieben.</h2>
          </m.div>
          <div className="mt-8"><ComparisonBlock /></div>
        </div>
      </section>

      {beforeProcess}

      {process}

      {beforeCta}

      {/* CTA */}
      <section className="py-12 lg:py-16" style={ctaTone === "secondary" ? { backgroundColor: "var(--bg-secondary)" } : undefined}>
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
          <m.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4 }}>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>Bereit, Budget in Anfragen zu verwandeln?</h2>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>Im Erstgespräch klären wir, welche Plattform für Ihre Zielgruppe passt – und mit welchem realistischen Budget Sie anfangen sollten.</p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link href="/beratung" className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all hover:bg-brand-600 hover:shadow-md">
                <Calendar className="h-5 w-5" /> Kostenloses Erstgespräch
              </Link>
              <Link href="/anfrage" className="inline-flex items-center gap-2 rounded-lg border px-5 py-3 text-base font-semibold transition-all hover:shadow-sm" style={{ color: "var(--text-secondary)", borderColor: "var(--border-secondary)" }}>
                Anfrage senden
              </Link>
            </div>
          </m.div>
        </div>
      </section>

      <section className="py-12 lg:py-16" style={ctaTone === "primary" ? { backgroundColor: "var(--bg-secondary)" } : undefined}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-center text-xl font-semibold" style={{ color: "var(--text)" }}>Das könnte Sie auch interessieren</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {[
              { name: "SEO – langfristig sichtbar", href: "/dienstleistungen/seo" },
              { name: "Social Media Marketing", href: "/dienstleistungen/social-media-marketing" },
              { name: "Webdesign – Landingpages", href: "/dienstleistungen/webdesign" },
            ].map((s) => (
              <Link key={s.href} href={s.href} className="group inline-flex items-center gap-2 rounded-xl border px-5 py-3 text-sm font-medium transition-all hover:shadow-sm" style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                {s.name}
                <ArrowRight className="h-3.5 w-3.5 text-brand-500 transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
